/**
 * 공유 인테이크 읽기 (PLAN 5-6장, M6).
 *
 * 서비스워커가 `share_target` POST를 가로채 `share-intake` 캐시에 임시 저장한다
 * (메타는 `/__share/{token}`, 파일 본문은 `/__share/{token}/file/{i}`).
 * 이 모듈은 그 캐시를 읽어 File로 되돌리는 **유일한** 경로다.
 *
 * 읽은 즉시 삭제한다 — 공유 내용은 사용자 기기 캐시에 남으면 안 된다(잠금 상태에서도).
 */

const CACHE_NAME = 'share-intake';

export interface SharePayload {
  title?: string;
  text?: string;
  url?: string;
  fileCount: number;
  receivedAt?: number;
}

export interface ShareIntake {
  meta: SharePayload;
  /** 원본 그대로 복원한 파일 — 없어도 된다(메타만 먼저 오는 경우) */
  files: File[];
}

/** 전송할 본문 한 줄로 합친다 — 공유 시트는 "무엇을 보낼지"를 미리 보여줘야 한다 */
export function shareTextOf(payload: SharePayload): string {
  return [payload.text, payload.title, payload.url].filter(Boolean).join('\n').trim();
}

export async function readShareIntake(token: string): Promise<ShareIntake | null> {
  const cache = await caches.open(CACHE_NAME);
  const metaRes = await cache.match(`/__share/${token}`);
  if (!metaRes) return null;

  const meta = (await metaRes.json()) as SharePayload;
  const files: File[] = [];
  for (let i = 0; i < meta.fileCount; i += 1) {
    const res = await cache.match(`/__share/${token}/file/${i}`);
    if (!res) continue;
    const name = decodeURIComponent(res.headers.get('x-file-name') ?? `shared-${i}`);
    const type = res.headers.get('content-type') ?? 'application/octet-stream';
    files.push(new File([await res.blob()], name, { type }));
  }

  return { meta: { ...meta, fileCount: files.length }, files };
}

/**
 * 처리 완료 후 임시 캐시 정리 — 같은 내용이 재사용되지 않도록.
 * 실패해도 전송 자체는 이미 끝난 뒤이므로 조용히 넘어간다.
 */
export async function clearShareIntake(token: string): Promise<void> {
  try {
    const cache = await caches.open(CACHE_NAME);
    const keys = await cache.keys();
    await Promise.all(
      keys.filter((req) => req.url.includes(`/__share/${token}`)).map((req) => cache.delete(req)),
    );
  } catch (error) {
    console.error('[talk2nIcK] 공유 캐시 정리 실패:', error);
  }
}
